import React, { useEffect, Suspense } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { KycProtectedRoute } from './KycProtectedRoute';
import { RoutingList } from './list';
import { LandingLayout } from '../../shared/components/layout/LandingLayout';
import { MainLayout } from '../../shared/components/layout/MainLayout';
import { AuthenticatedLayout } from '../../shared/components/layout/AuthenticatedLayout';
import { DynamicLandingWrapper } from '../../shared/components/layout/DynamicLandingWrapper';
import { getUserTypeFromPath } from '../../shared/utils/userTypeUtils';

const AppRoutes: React.FC = () => {
  const navigate = useNavigate();

  // Listen for custom navigation event from error interceptor
  useEffect(() => {
    const handleNavigateToReportIssue = () => {
      navigate('/update-profile?tab=report-issue', { replace: true });
    };

    window.addEventListener('navigate-to-report-issue', handleNavigateToReportIssue);

    return () => {
      window.removeEventListener('navigate-to-report-issue', handleNavigateToReportIssue);
    };
  }, [navigate]);

  return (
    <Suspense fallback={null}>
      <Routes>
        {RoutingList.map((route, index) => {
          const PageComponent = route.component;
          let element = route.element ?? (PageComponent ? <PageComponent /> : null);
          const showHeader = route.showHeader ?? true;
          const showFooter = route.showFooter ?? true;
          const showFAB = route.showFAB ?? false;

          if (route.layout === 'dynamic') {
            element = (
              <DynamicLandingWrapper defaultComponent={element} />
            );
          } else if (route.layout === 'landing') {
            const userType = getUserTypeFromPath(route.route);
            element = (
              <LandingLayout
                showHeader={showHeader}
                showFooter={showFooter}
                showFAB={showFAB}
                userType={userType || undefined}
              >
                {element}
              </LandingLayout>
            );
          } else if (route.layout === 'authenticated') {
            element = (
              <AuthenticatedLayout
                showHeader={showHeader}
                showFooter={showFooter}
                showFAB={showFAB}
              >
                {element}
              </AuthenticatedLayout>
            );
          } else if (route.layout === 'main') {
            element = (
              <MainLayout
                showHeader={showHeader}
                showFooter={showFooter}
                showFAB={showFAB}
              >
                {element}
              </MainLayout>
            );
          }

          if (route.isProtected) {
            element = (
              <ProtectedRoute allowedRoles={route.allowedRoles}>
                <KycProtectedRoute>
                  {element}
                </KycProtectedRoute>
              </ProtectedRoute>
            );
          }

          return <Route key={`route-${index}`} path={route.route} element={element} />;
        })}
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;